<template>
  <modal
    :show="isVisible"
    :title="modalTitle"
    elem-id="modal_site_templates"
    size="large"
    :close-label="t('Cancelar')"
    :save-label="isSaving ? t('Salvando') : t('Salvar')"
    :save-fn="handleSave"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <site-templates-form
        ref="formRef"
        :templateId="currentTemplateId"
        :isEditMode="isEditMode"
        @submit-success="handleFormSuccess"
        @submit-error="handleFormError"
        @validation-failed="handleValidationFailed"
      />
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SiteTemplatesForm from './SiteTemplatesForm.vue';
import { log, warn as logWarn, error as logError } from '../../utils/DebugLogger';

type TemplateId = number | string;

interface SiteTemplatesFormInstance {
  resetForm: () => void;
  loadForEdit: () => Promise<void>;
  handleSubmit: () => Promise<void>;
  validateForm: () => boolean;
}

interface ModalOpenPayload {
  id?: TemplateId;
  action?: 'create' | 'edit';
}

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const currentTemplateId = ref<TemplateId | null>(null);
const formRef = ref<SiteTemplatesFormInstance | null>(null);
const isSaving = ref<boolean>(false);

const modalTitle = computed<string>(() =>
  isEditMode.value ? t('Editar template de e-mail') : t('Adicionar template de e-mail')
);

const openForCreate = (): void => {
  log('SiteTemplatesModal', 'openForCreate', 'Opening modal for create');
  isVisible.value = true;
  isEditMode.value = false;
  currentTemplateId.value = null;

  nextTick(() => {
    if (formRef.value) {
      formRef.value.resetForm();
    }
  });
};

const openForEdit = async (templateId: TemplateId): Promise<void> => {
  log('SiteTemplatesModal', 'openForEdit', 'Opening modal for edit', templateId);
  isVisible.value = true;
  isEditMode.value = true;
  currentTemplateId.value = templateId;

  await nextTick();

  if (formRef.value) {
    await formRef.value.loadForEdit();
  }
};

const handleClose = (): void => {
  log('SiteTemplatesModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentTemplateId.value = null;
  isSaving.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('SiteTemplatesModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    isSaving.value = true;
    try {
      await formRef.value.handleSubmit();
    } finally {
      isSaving.value = false;
    }
  }
};

const handleFormSuccess = (): void => {
  log('SiteTemplatesModal', 'handleFormSuccess', 'Form submitted successfully');
  eventBus.emit('site-templates.saved');
  handleClose();
};

const handleFormError = (error: unknown): void => {
  logError('SiteTemplatesModal', 'handleFormError', 'Form submission error', error);
};

const handleValidationFailed = (): void => {
  logWarn('SiteTemplatesModal', 'handleValidationFailed', 'Client-side validation failed');
};

const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('SiteTemplatesModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id !== undefined && data?.id !== null) {
    void openForEdit(data.id);
  } else {
    openForCreate();
  }
};

onMounted(() => {
  log('SiteTemplatesModal', 'onMounted', 'Component mounted, setting up event listeners');
  eventBus.on('modal.site-templates.open', handleModalOpen);
  eventBus.on('modal.site-templates.close', handleClose);
});

onBeforeUnmount(() => {
  log('SiteTemplatesModal', 'onBeforeUnmount', 'Component unmounting, cleaning up event listeners');
  eventBus.off('modal.site-templates.open', handleModalOpen);
  eventBus.off('modal.site-templates.close', handleClose);
});

defineExpose({
  openForCreate,
  openForEdit
});
</script>

<style scoped>
</style>
